import React, { useState, useEffect, useRef, useCallback } from "react";
import { db, dbRealtime } from "../firebase/firebase";
import logo from "../assets/svg/khotba_logo.svg";
import {
  onSnapshot,
  query,
  collection,
  where,
  doc,
  getDocs,
} from "firebase/firestore";
import { ref, onChildAdded, onChildChanged } from "firebase/database";
import "../assets/css/App.css";

function TranslationCardWithScroll({ language, translationArray, isRTL }) {
  const bodyRef = useRef(null);
  const scrollSignature = translationArray
    .map(
      (t) =>
        `${t.startMs}:${t.text}:${t.isFinal ? 1 : 0}:${t.isUpdated ? 1 : 0}`
    )
    .join("|");

  useEffect(() => {
    const el = bodyRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [scrollSignature]);

  const combinedText = translationArray.map((t) => (
    <span
      key={t.startMs}
      style={{ color: t.isFinal ? "white" : "yellow" }}
    >
      {t.text}{" "}
    </span>
  ));

  return (
    <div
      className="translation-card"
      style={{
        direction: isRTL ? "rtl" : "ltr",
        textAlign: isRTL ? "right" : "left",
      }}
    >
      <div className="language-header">
        <span className="language-name">{language}</span>
      </div>
      <div className="translation-card-body" ref={bodyRef}>
        <p className="translated-text">
          {combinedText.length > 0 ? combinedText : "...."}
        </p>
      </div>
    </div>
  );
}

function App() {
  const [sessionId, setSessionId] = useState("");
  const [khotba, setKhotba] = useState(null);
  const [error, setError] = useState("");
  const [translations, setTranslations] = useState({});
  const [languages, setLanguages] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [showLanguageSelection, setShowLanguageSelection] = useState(false);
  const [listenerDocId, setListenerDocId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeSubs, setActiveSubs] = useState([]);

  const [translationQueues, setTranslationQueues] = useState({}); // eslint-disable-line no-unused-vars
  const [currentTranslations, setCurrentTranslations] = useState({}); // eslint-disable-line no-unused-vars
  const [prevTranslations, setPrevTranslations] = useState({}); // eslint-disable-line no-unused-vars

  const inputRef = useRef(null);
  const langButtonsRef = useRef([]);
  /** Unsubscribe functions returned by RTDB onChildAdded / onChildChanged */
  const rtdbUnsubsRef = useRef([]);
  const baseUrl = process.env.REACT_APP_FIREBASE_baseurl;

  const detachAllRtdbListeners = useCallback(() => {
    rtdbUnsubsRef.current.forEach((unsub) => {
      try {
        unsub();
      } catch (e) {
        console.error("RTDB unsubscribe error:", e);
      }
    });
    rtdbUnsubsRef.current = [];
  }, []);

  const clearTranslationState = () => {
    detachAllRtdbListeners();
    setTranslations({});
    setTranslationQueues({});
    setCurrentTranslations({});
    setPrevTranslations({});
    setActiveSubs([]);
  };

  const handleSessionIdSubmit = async () => {
    if (!sessionId.trim()) {
      setError("Please enter a valid session ID.");
      return;
    }

    setLoading(true);
    setError("");
    clearTranslationState();

    try {
      const q = query(
        collection(db, "khotbas"),
        where("sessionId", "==", sessionId)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError("No Khutbah found with this session ID.");
        setKhotba(null);
        setLanguages([]);
        setSelectedLanguages([]);
        setLoading(false);
        return;
      }

      const khotbaDoc = querySnapshot.docs[0];
      const khotbaData = khotbaDoc.data();
      const khotbaId = khotbaDoc.id;

      if (khotbaData.khotbaType === "Ended") {
        setError("This Khutbah has ended and cannot be joined.");
        setKhotba(null);
        setLanguages([]);
        setSelectedLanguages([]);
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${baseUrl}joinTvListener?khotbaId=${khotbaId}`
      );

      const data = await response.json();
      if (!response.ok) {
        setError(data.error || "Failed to join the khutbah.");
        setLoading(false);
        return;
      }

      setListenerDocId({ id: data.id, khotbaId: data.khotbaId });

      let langs = [];
      if (khotbaData.isOneTimePaid) {
        langs = khotbaData.selectedLanguages || [];
      } else {
        try {
          const response = await fetch(
            `${baseUrl}getSupportedLanguages?khatibId=${khotbaData.creatorId}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            }
          );
          if (!response.ok) throw new Error("Error fetching languages");
          const data = await response.json();
          langs = data.languages || [];
        } catch {
          langs = [];
        }
      }

      setLanguages(langs);
      setKhotba({ id: khotbaId, ...khotbaData });
      setShowLanguageSelection(true);
      setError("");
      setLoading(false);
    } catch (err) {
      console.error("Error fetching Khutbah:", err);
      setError(
        err.code === "permission-denied"
          ? "You don't have permission to access this Khutbah."
          : err.code === "unavailable"
          ? "Network error. Please check your connection and try again."
          : "Failed to fetch Khutbah. Please try again."
      );
      setLoading(false);
    }
  };

  const handleLanguageSelection = () => {
    if (selectedLanguages.length === 0) {
      setError("Please select at least one language to continue.");
      return;
    }
    setError("");
    setShowLanguageSelection(false);
    setTranslations({});

    if (khotba) {
      const subs = setupRealtimeListeners(khotba.id, selectedLanguages);
      setActiveSubs(subs);
    }
  };

  const setupRealtimeListeners = (khotbaId, languages) => {
    detachAllRtdbListeners();

    const unsubs = [];
    languages.forEach((language) => {
      const textsRef = ref(
        dbRealtime,
        `transcriptions/${khotbaId}/${language.toLowerCase()}/texts`
      );

      const unsubAdded = onChildAdded(textsRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const startMs = parseInt(snapshot.key, 10);
          const { text, isFinal, isUpdated } = data;

          if (text) {
            setTranslations((prev) => {
              const updated = { ...prev };
              if (!updated[language]) updated[language] = [];
              const index = updated[language].findIndex(
                (t) => t.startMs === startMs
              );
              if (index !== -1) {
                updated[language][index] = {
                  text,
                  startMs,
                  isFinal,
                  isUpdated,
                };
              } else {
                updated[language].push({ text, startMs, isFinal, isUpdated });
              }
              updated[language].sort((a, b) => a.startMs - b.startMs);
              return updated;
            });
          }
        }
        setLoading(false);
      });

      const unsubChanged = onChildChanged(textsRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const startMs = parseInt(snapshot.key, 10);
          const { text, isFinal, isUpdated } = data;

          if (text) {
            setTranslations((prev) => {
              const updated = { ...prev };
              if (!updated[language]) updated[language] = [];
              const index = updated[language].findIndex(
                (t) => t.startMs === startMs
              );
              if (index !== -1) {
                updated[language][index] = {
                  text,
                  startMs,
                  isFinal,
                  isUpdated,
                };
              } else {
                updated[language].push({ text, startMs, isFinal, isUpdated });
              }
              updated[language].sort((a, b) => a.startMs - b.startMs);
              return updated;
            });
          }
        }
      });

      unsubs.push(unsubAdded, unsubChanged);
    });

    rtdbUnsubsRef.current = unsubs;
    return languages.map((lang) => ({ language: lang }));
  };

  const handleExit = async () => {
    setLoading(true);
    detachAllRtdbListeners();
    try {
      if (listenerDocId) {
        const url = `${baseUrl}deleteListener?khotbaId=${listenerDocId.khotbaId}&listenerId=${listenerDocId.id}`;
        try {
          const response = await fetch(url, { method: "GET" });
          if (!response.ok) {
            throw new Error(`Error: ${response.status} ${response.statusText}`);
          }
          const result = await response.text();
          console.log("Server response:", result);
        } catch (error) {
          console.error("Failed to delete listener:", error);
        }
      }
      setKhotba(null);
      setActiveSubs([]);
      setLanguages([]);
      setSelectedLanguages([]);
      setShowLanguageSelection(false);
      setSessionId("");
      setListenerDocId(null);
      setTranslations({});
      setTranslationQueues({});
      setCurrentTranslations({});
      setPrevTranslations({});
      setError("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => () => detachAllRtdbListeners(), [detachAllRtdbListeners]);

  useEffect(() => {
    if (!khotba) return;

    setLoading(true);

    const translationsRef = collection(
      db,
      "khotbas",
      khotba.id,
      "translations"
    );

    const unsubscribeTranslations = onSnapshot(
      query(translationsRef),
      (snapshot) => {
        setTranslationQueues((prev) => {
          const updated = { ...prev };
          snapshot.forEach((docSnap) => {
            const lang = docSnap.id;
            const data = docSnap.data();
            const latest = data.translations?.[data.translations.length - 1];
            if (latest) {
              if (!updated[lang]) updated[lang] = [];
              // Avoid duplicate consecutive entries
              const lastInQueue = updated[lang][updated[lang].length - 1];
              if (!lastInQueue || lastInQueue.text !== latest.text) {
                updated[lang] = [...updated[lang], latest];
              }
            }
          });
          return updated;
        });
        setLoading(false);
      },
      () => {
        setError("Failed to fetch translations.");
        setLoading(false);
      }
    );

    const khotbaDocRef = doc(db, "khotbas", khotba.id);
    const unsubscribeStatus = onSnapshot(khotbaDocRef, (docSnap) => {
      const khotbaData = docSnap.data();
      if (khotbaData?.khotbaType === "Ended") {
        detachAllRtdbListeners();
        setActiveSubs([]);
        setKhotba(null);
        setTranslations({});
        setTranslationQueues({});
        setCurrentTranslations({});
        setPrevTranslations({});
        setLanguages([]);
        setSelectedLanguages([]);
        setShowLanguageSelection(false);
        setSessionId("");
        setListenerDocId(null);
        setError("This Khutbah has ended.");
      }
    });

    return () => {
      unsubscribeTranslations();
      unsubscribeStatus();
    };
  }, [khotba, activeSubs, detachAllRtdbListeners]);

  const [focusedLangIndex, setFocusedLangIndex] = useState(0);

  useEffect(() => {
    setFocusedLangIndex((i) => {
      if (languages.length === 0) return 0;
      return Math.min(i, languages.length - 1);
    });
  }, [languages]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showLanguageSelection && languages.length > 0) {
        if (e.key === "ArrowRight") {
          setFocusedLangIndex((prev) => {
            const safe = Math.min(Math.max(0, prev), languages.length - 1);
            return (safe + 1) % languages.length;
          });
        } else if (e.key === "ArrowLeft") {
          setFocusedLangIndex((prev) => {
            const safe = Math.min(Math.max(0, prev), languages.length - 1);
            return safe === 0 ? languages.length - 1 : safe - 1;
          });
        } else if (e.key === "Enter") {
          const idx = Math.min(
            Math.max(0, focusedLangIndex),
            languages.length - 1
          );
          toggleLanguage(languages[idx]);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [languages, showLanguageSelection, focusedLangIndex]);

  useEffect(() => {
    const handleUnload = () => {
      if (listenerDocId) {
        const url = `${baseUrl}deleteListener?khotbaId=${listenerDocId.khotbaId}&listenerId=${listenerDocId.id}`;
        navigator.sendBeacon(url);
      }
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [listenerDocId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleLanguage = (lang) => {
    setSelectedLanguages((prev) =>
      prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang]
    );
  };

  return (
    <div className="App">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-text">Loading...</p>
          </div>
        </div>
      )}

      <div className="logo-container">
        <img src={logo} alt="Khutbah Logo" className="logo" />
      </div>

      {!khotba ? (
        <div className="session-id-container">
          <h2>Enter Session ID</h2>
          <input
            ref={inputRef}
            type="text"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
            placeholder="Session ID"
            className="session-id-input"
          />
          <button onClick={handleSessionIdSubmit} className="submit-button">
            Join Khutbah
          </button>
          {error && <p className="error">{error}</p>}
        </div>
      ) : showLanguageSelection ? (
        <div className="session-id-container">
          <h2>Select Languages to Display</h2>
          <div className="language-selection">
            {languages.map((lang, index) => (
              <button
                key={lang}
                ref={(el) => (langButtonsRef.current[index] = el)}
                onClick={() => toggleLanguage(lang)}
                className={`language-button ${
                  selectedLanguages.includes(lang) ? "selected" : ""
                }`}
              >
                {lang}
              </button>
            ))}
          </div>
          <div className="button-group">
            <button
              onClick={handleLanguageSelection}
              className="submit-button"
              style={{ marginTop: "20px" }}
            >
              Show Translations
            </button>
            <button
              onClick={handleExit}
              className="exit-button"
              style={{ marginTop: "10px" }}
            >
              Exit
            </button>
            {error && <p className="error">{error}</p>}
          </div>
        </div>
      ) : (
        <div className="translation-container">
          <h2>Live Khutbah Translation: {khotba?.khotbaName}</h2>
          <div className="button-group">
            <button
              onClick={handleExit}
              className="exit-button"
              style={{ marginBottom: "20px" }}
            >
              Exit
            </button>
            {error && <p className="error">{error}</p>}
          </div>

          {selectedLanguages.length > 0 ? (
            <div className="translation-list">
              {selectedLanguages.map((language) => {
                const translationArray = translations[language] || [];
                const isRTL = ["Arabic", "Persian", "Urdu"].includes(language);
                return (
                  <TranslationCardWithScroll
                    key={language}
                    language={language}
                    translationArray={translationArray}
                    isRTL={isRTL}
                  />
                );
              })}
            </div>
          ) : (
            <p>No languages selected for display.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default App;
